import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentAppUser } from '@/hooks/useCurrentAppUser';

interface PerformanceMetrics {
  lcpMs?: number;
  fcpMs?: number;
  cls?: number;
  fidMs?: number;
  inpMs?: number;
  ttfbMs?: number;
  pageLoadMs?: number;
  domInteractiveMs?: number;
  domCompleteMs?: number;
  resourcesLoaded?: number;
}

function getDeviceType(): 'desktop' | 'mobile' | 'tablet' {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) return 'tablet';
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated/.test(ua)) return 'mobile';
  return 'desktop';
}

function getConnectionType(): string | undefined {
  const nav = navigator as any;
  return nav.connection?.effectiveType;
}

export function usePerformanceMonitor(sessionId: string | null) {
  const { data: appUser } = useCurrentAppUser();
  const location = useLocation();
  const reportedPathsRef = useRef<Set<string>>(new Set());
  const metricsRef = useRef<PerformanceMetrics>({});

  // Collect Web Vitals using PerformanceObserver
  useEffect(() => {
    if (typeof window === 'undefined' || !('PerformanceObserver' in window)) return;

    const observers: PerformanceObserver[] = [];

    try {
      // LCP Observer
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1] as any;
        if (lastEntry?.startTime) {
          metricsRef.current.lcpMs = Math.round(lastEntry.startTime);
        }
      });
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
      observers.push(lcpObserver);
    } catch (e) {
      // LCP not supported
    }

    try {
      // FCP Observer
      const fcpObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.name === 'first-contentful-paint') {
            metricsRef.current.fcpMs = Math.round(entry.startTime);
          }
        }
      });
      fcpObserver.observe({ type: 'paint', buffered: true });
      observers.push(fcpObserver);
    } catch (e) {
      // FCP not supported
    }

    try {
      // CLS Observer
      let clsValue = 0;
      const clsObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries() as any[]) {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
          }
        }
        metricsRef.current.cls = Math.round(clsValue * 1000) / 1000;
      });
      clsObserver.observe({ type: 'layout-shift', buffered: true });
      observers.push(clsObserver);
    } catch (e) {
      // CLS not supported
    }

    try {
      // FID Observer
      const fidObserver = new PerformanceObserver((list) => {
        const firstInput = list.getEntries()[0] as any;
        if (firstInput?.processingStart && firstInput?.startTime) {
          metricsRef.current.fidMs = Math.round(firstInput.processingStart - firstInput.startTime);
        }
      });
      fidObserver.observe({ type: 'first-input', buffered: true });
      observers.push(fidObserver);
    } catch (e) {
      // FID not supported
    }

    return () => {
      observers.forEach(obs => obs.disconnect());
    };
  }, []);

  // Collect Navigation Timing
  const collectNavigationMetrics = useCallback(() => {
    try {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (!navigation) return;

      metricsRef.current = {
        ...metricsRef.current,
        ttfbMs: Math.round(navigation.responseStart),
        domInteractiveMs: Math.round(navigation.domInteractive),
        domCompleteMs: Math.round(navigation.domComplete),
        pageLoadMs: navigation.loadEventEnd > 0 
          ? Math.round(navigation.loadEventEnd - navigation.startTime)
          : undefined,
        resourcesLoaded: performance.getEntriesByType('resource').length,
      };
    } catch (e) {
      console.error('[PerformanceMonitor] Navigation metrics error:', e);
    }
  }, []);

  // Report metrics to Supabase
  const reportMetrics = useCallback(async () => {
    if (!appUser?.id || !sessionId) return;
    
    const path = location.pathname;
    
    // Avoid reporting the same page multiple times
    if (reportedPathsRef.current.has(path)) return;
    reportedPathsRef.current.add(path);

    collectNavigationMetrics();

    const metrics = metricsRef.current;
    
    // Only report if we have at least some metrics
    if (!metrics.pageLoadMs && !metrics.lcpMs && !metrics.fcpMs) return;

    try {
      const { error } = await supabase.from('performance_metrics').insert({
        session_id: sessionId,
        user_id: appUser.id,
        page_path: path,
        device_type: getDeviceType(),
        connection_type: getConnectionType(),
        lcp_ms: metrics.lcpMs,
        fcp_ms: metrics.fcpMs,
        cls: metrics.cls,
        fid_ms: metrics.fidMs,
        ttfb_ms: metrics.ttfbMs,
        page_load_ms: metrics.pageLoadMs,
        dom_interactive_ms: metrics.domInteractiveMs,
        dom_complete_ms: metrics.domCompleteMs,
        resources_loaded: metrics.resourcesLoaded,
        user_agent: navigator.userAgent,
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
      });

      if (error) {
        console.error('[PerformanceMonitor] Error reporting metrics:', error);
      } else {
        console.log('[PerformanceMonitor] Metrics reported for:', path);
      }
    } catch (err) {
      console.error('[PerformanceMonitor] Error reporting metrics:', err);
    }
  }, [appUser?.id, sessionId, location.pathname, collectNavigationMetrics]);

  // Report on page load (after a delay to collect all metrics)
  useEffect(() => {
    const timeout = setTimeout(reportMetrics, 3000); // Wait 3s for metrics to stabilize
    return () => clearTimeout(timeout);
  }, [location.pathname, reportMetrics]);

  // Reset metrics when path changes for fresh collection
  useEffect(() => {
    metricsRef.current = {};
  }, [location.pathname]);

  return { reportMetrics };
}
